import React from 'react';
import { Player, PlayerStats } from '../../types';
import { PlayerCard } from './PlayerCard';
import { EmptySlot } from './EmptySlot';
import { useResponsive } from '../../hooks/useResponsive';

interface TeamProps {
  teamId: string;
  teamName: string;
  players: (Player | null)[];
  backgroundColor: string;
  borderColor: string;
  titleColor: string;
  teamIndex: number;
  userIsCreator: boolean;
  occupyingSpaces: { [key: string]: boolean };
  onRemovePlayer: (playerId: string) => void;
  onSelfRemove?: (playerId: string) => void;
  onEditPlayer?: (player: Player) => void;
  onCreateReview?: (player: Player) => void;
  onViewReviews?: (player: Player) => void;
  onViewProfile?: (player: Player) => void;
  currentUserId?: string;
  canViewReviews?: boolean;
  onJoinPosition: (teamIndex: number, positionIndex: number) => void;
  getSpaceKey: (teamIndex: number, positionIndex: number) => string;
  playerStats?: { [playerId: string]: PlayerStats }; // Stats por jugador
  matchStatus?: string; // Estado del partido
  mvpPlayerIds?: string[]; // <-- ids de los MVPs
  topScorerPlayerIds?: string[]; // <-- ids de los goleadores
  topAssisterPlayerIds?: string[]; // <-- ids de los asistentes
  topSaverPlayerIds?: string[]; // <-- ids de los jugadores con más atajadas
  creatorId?: string; // ID del creador del partido
  reportCompleted?: boolean; // Si el reporte está finalizado
  // Nuevas props para drag & drop
  onDragStart?: (player: Player) => void;
  onDragEnd?: () => void;
  onDragOver?: (player: Player | null) => void;
  onDrop?: (player: Player | null) => void;
  isDragging?: boolean;
  draggedPlayer?: Player | null;
  dragOverPlayer?: Player | null;
  // Props para reviews
  hasReviewedPlayer?: { [playerId: string]: boolean }; // Mapa de jugadores ya reviewados
  loadingReviews?: boolean; // Estado de loading para reviews
  // Props para validación de stats
  onValidateStats?: (player: Player) => void;
  hasPendingStats?: { [playerId: string]: boolean }; // Mapa de jugadores con stats pendientes
  // Props para editar nombres de equipos
  onEditTeamNames?: () => void;
  // Props para mostrar equipo ganador
  isWinningTeam?: boolean;
  // Props para mostrar marcador final
  teamGoals?: number;
  isDraw?: boolean; // Nuevo prop para indicar si el partido es un empate
  // Props para organizer panel
  allPlayers?: Player[];
  onSetPlayerSkill?: (playerId: string, score: number) => void;
  onLinkFriends?: (playerAId: string, playerBId: string) => void;
  onUnlinkFriend?: (playerId: string) => void;
  onGenerateReplacementLink?: (player: Player) => void;
}

export const Team: React.FC<TeamProps> = ({
  teamId,
  teamName,
  players,
  backgroundColor,
  borderColor,
  titleColor,
  teamIndex,
  userIsCreator,
  occupyingSpaces,
  onRemovePlayer,
  onSelfRemove,
  onEditPlayer,
  onCreateReview,
  onViewReviews,
  onViewProfile,
  currentUserId,
  canViewReviews,
  onJoinPosition,
  getSpaceKey,
  playerStats,
  matchStatus,
  mvpPlayerIds = [],
  topScorerPlayerIds = [],
  topAssisterPlayerIds = [],
  topSaverPlayerIds = [],
  creatorId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging = false,
  draggedPlayer,
  dragOverPlayer,
  hasReviewedPlayer = {},
  loadingReviews = false,
  onValidateStats,
  hasPendingStats = {},
  onEditTeamNames,
  isWinningTeam = false,
  teamGoals,
  isDraw,
  allPlayers,
  onSetPlayerSkill,
  onLinkFriends,
  onUnlinkFriend,
  onGenerateReplacementLink,
}) => {
  const handleDragStart = (e: React.DragEvent, player: Player) => {
    if (!userIsCreator) {
      e.preventDefault();
      return;
    }
    
    // Configurar drag preview personalizado
    e.dataTransfer.setData('text/plain', player.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Crear un elemento invisible para el drag preview nativo
    const dragImage = document.createElement('div');
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.left = '-1000px';
    dragImage.style.width = '1px';
    dragImage.style.height = '1px';
    dragImage.style.opacity = '0';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    
    // Remover el elemento después de un frame
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
    
    onDragStart?.(player);
  };

  const handleDrop = (e: React.DragEvent, player: Player | null) => {
    if (!userIsCreator || !draggedPlayer) {
      e.preventDefault();
      return;
    }
    
    // Si el jugador arrastrado es el mismo que el objetivo, no hacer nada
    if (player && draggedPlayer.id === player.id) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    onDrop?.(player);
  };

  const handleDragOver = (e: React.DragEvent, player: Player | null) => {
    if (!userIsCreator || !draggedPlayer) {
      e.preventDefault();
      return;
    }
    
    // Si el jugador arrastrado es el mismo que el objetivo, no permitir
    if (player && draggedPlayer.id === player.id) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(player);
  };

  const handleDragEnd = () => {
    onDragEnd?.();
  };

  const { isMobile, isCompact } = useResponsive();
  
  return (
    <div
      style={{
        flex: 1,
        minWidth: isMobile || isCompact ? '100%' : '300px',
        padding: isMobile || isCompact ? '12px' : '12px 16px',
        backgroundColor,
        borderRadius: '18px',
        border: `2px solid ${borderColor}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        width: isMobile || isCompact ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ 
        fontSize: isMobile || isCompact ? '16px' : '18px',
        fontWeight: 'bold',
        marginBottom: isMobile || isCompact ? '12px' : '10px',
        color: titleColor,
        textAlign: 'center',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        letterSpacing: '0.08em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile || isCompact ? '6px' : '8px',
        flexWrap: isMobile || isCompact ? 'wrap' : 'nowrap'
      }}>
        {teamGoals !== undefined && matchStatus === 'FINISHED' && teamGoals > 0 && (
          <span style={{
            backgroundColor: '#2C2F33',
            color: '#FFFFFF',
            padding: isMobile || isCompact ? '2px 6px' : '3px 8px',
            borderRadius: '6px',
            fontSize: isMobile || isCompact ? '12px' : '14px',
            fontWeight: 'bold',
            marginRight: '6px'
          }}>
            {isWinningTeam ? `+${teamGoals}` : teamGoals}
          </span>
        )}
        <span>{teamName}</span>
        {isWinningTeam && matchStatus === 'FINISHED' && !isDraw && (
          <span style={{
            backgroundColor: '#00E676',
            color: '#181A1B',
            padding: isMobile || isCompact ? '2px 6px' : '3px 8px',
            borderRadius: '6px',
            fontSize: isMobile || isCompact ? '10px' : '12px',
            fontWeight: 'bold',
            marginLeft: '4px'
          }}>
            🏆 Ganador!
          </span>
        )}
        {isDraw && matchStatus === 'FINISHED' && (
          <span style={{
            backgroundColor: '#FFD600',
            color: '#181A1B',
            padding: isMobile || isCompact ? '2px 6px' : '3px 8px',
            borderRadius: '6px',
            fontSize: isMobile || isCompact ? '10px' : '12px',
            fontWeight: 'bold',
            marginLeft: '4px'
          }}>
            🤝 Empate!
          </span>
        )}
        {userIsCreator && onEditTeamNames && matchStatus !== 'FINISHED' && (
          <button
            onClick={onEditTeamNames}
            style={{
              background: 'none',
              border: 'none',
              color: titleColor,
              cursor: 'pointer',
              fontSize: isMobile || isCompact ? '12px' : '14px',
              padding: isMobile || isCompact ? '1px 4px' : '2px 6px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile || isCompact ? '2px' : '4px',
              opacity: 0.7
            }}
            title="Editar nombre del equipo"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
          >
            ✏️
          </button>
        )}
      </div>
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile || isCompact ? undefined : '2px',
        flex: isMobile || isCompact ? undefined : 1,
        minHeight: 0,
      }}>
      {players?.map((player, idx) => {
        const spaceKey = getSpaceKey(teamIndex, idx);
        const isOccupying = occupyingSpaces[spaceKey];
        const isDragOver = dragOverPlayer?.id === player?.id;
        const isBeingDragged = draggedPlayer?.id === player?.id;

        if (player) {
          return (
            <div
              key={`player-${player.id}`}
              draggable={userIsCreator}
              onDragStart={(e) => handleDragStart(e, player)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, player)}
              onDrop={(e) => handleDrop(e, player)}
              style={{
                opacity: isBeingDragged ? 0.5 : 1,
                transform: isDragOver ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.2s ease',
                position: 'relative',
                flex: isMobile || isCompact ? undefined : 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {isDragOver && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 215, 0, 0.3)',
                  border: '2px dashed #FFD700',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#FFD700',
                  textShadow: '0 0 4px rgba(0,0,0,0.8)'
                }}>
                  🔄 INTERCAMBIAR
                </div>
              )}
              <PlayerCard
                player={player}
                userIsCreator={userIsCreator}
                onRemovePlayer={onRemovePlayer}
                onSelfRemove={onSelfRemove}
                onEditPlayer={onEditPlayer}
                onCreateReview={onCreateReview}
                onViewReviews={onViewReviews}
                onViewProfile={onViewProfile}
                onValidateStats={onValidateStats}
                currentUserId={currentUserId}
                playerStats={playerStats?.[player.id]}
                matchStatus={matchStatus}
                isMVP={mvpPlayerIds.includes(player.id)}
                isTopScorer={topScorerPlayerIds.includes(player.id)}
                isTopAssister={topAssisterPlayerIds.includes(player.id)}
                isTopSaver={topSaverPlayerIds.includes(player.id)}
                isMatchCreator={creatorId === player.userId}
                isDragging={isDragging}
                isDragOver={isDragOver}
                canViewReviews={canViewReviews}
                hasReviewedPlayer={hasReviewedPlayer[player.id] || false}
                loadingReviews={loadingReviews}
                hasPendingStats={hasPendingStats[player.id] || false}
                allPlayers={allPlayers}
                onSetPlayerSkill={onSetPlayerSkill}
                onLinkFriends={onLinkFriends}
                onUnlinkFriend={onUnlinkFriend}
                onGenerateReplacementLink={onGenerateReplacementLink}
              />
            </div>
          );
        } else {
          return (
            <div
              key={`empty-${teamId}-${idx}`}
              style={{
                flex: isMobile || isCompact ? undefined : 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <EmptySlot
                index={idx}
                isOccupying={isOccupying}
                userIsCreator={userIsCreator}
                onJoinPosition={() => onJoinPosition(teamIndex, idx)}
                matchStatus={matchStatus}
                onDragOver={onDragOver}
                onDrop={onDrop}
                isDragging={isDragging}
                draggedPlayer={draggedPlayer}
                dragOverPlayer={dragOverPlayer}
                teamIndex={teamIndex}
              />
            </div>
          );
        }
      })}
      </div>
    </div>
  );
}; 